/**
 * What a store pog's generation was SENT, for the Aesthetics checks: its shelf restrictions
 * (the band check), its core range (the unplaced check) and its blocking (a plan with no CDT
 * release is graded on its own columns in the blocking it was solved in).
 *
 * The backend keeps every store pog's pogger request (`output_store_pog.pogger_request_path`)
 * and hands out a signed URL for it on the store pog's logs route. Its
 * `fixtures[].shelves[].variants_restrictions` and `min_distribution` are what the solve was
 * given; the fences and core flags stored on the pog can disagree with them.
 */
import {
  RouteGetMerchflowStorePog_storePogId_logs,
  type ResponseGetMerchflowStorePog_storePogId_logs,
} from "@CommonApi/merchflow/store/pog/_storePogId_/logs";
import { API, getRequest } from "src/utils/axios";
import type { CoreRange } from "@CommonUtils/cdtStudio/domain/aestheticsScore";
import type { BlockingAxis } from "@CommonUtils/cdtStudio/domain/aestheticsRules";
import {
  blockingFromRequest,
  coreRangeFromRequest,
  sentRestrictionsFromRequest,
  type SentRestrictions,
} from "@CommonUtils/cdtStudio/domain/pogAesthetics";
import { reviewCoreRangeFromRequest } from "src/pages/SpacePlanning/review/reviewCoreRange";

export interface StorePogSentRequest {
  restrictions: SentRestrictions | null;
  coreRange: CoreRange | null;
  reviewCoreRange?: CoreRange;
  blocking: BlockingAxis | null;
  /** Each candidate's minimum days of supply, by product code (`sentMinDaysFromRequest`). */
  minDays?: ReadonlyMap<string, number | null> | null;
}

/**
 * Each candidate's `min_days_of_supply` as the request sent it, by product code (its `item_id`),
 * as owa-pog-agent's `bounds_from_request` reads it: null without a candidate list, a candidate
 * that sends none has null. The fixes hold a cut product to it (`FixSetting.sentMinDays`).
 */
export const sentMinDaysFromRequest = (
  request: unknown,
): ReadonlyMap<string, number | null> | null => {
  const candidates = (request as { candidates?: unknown } | null)?.candidates;
  if (!Array.isArray(candidates)) return null;
  const minDays = new Map<string, number | null>();
  for (const candidate of candidates) {
    const { item_id: itemId, min_days_of_supply: days } = (candidate ?? {}) as {
      item_id?: unknown;
      min_days_of_supply?: unknown;
    };
    if (itemId === null || itemId === undefined) continue;
    minDays.set(String(itemId), typeof days === "number" && Number.isFinite(days) ? days : null);
  }
  return minDays;
};

export const fetchStorePogSentRequest = async (
  storePogId: number,
  signal?: AbortSignal,
): Promise<StorePogSentRequest | null> => {
  const { data } = await getRequest<ResponseGetMerchflowStorePog_storePogId_logs>(
    `${API}${RouteGetMerchflowStorePog_storePogId_logs}`,
    { params: { storePogId }, signal },
  );
  const requestUrl = data?.pogger_request_path;
  if (!requestUrl) return null;
  const response = await fetch(requestUrl, { signal });
  // A URL means an archived request exists. A failed fetch is not the same as
  // a plan that genuinely has no archived request: grading without its input
  // would silently omit the restrictions the solve received.
  if (!response.ok) throw new Error("The archived generation request could not be loaded.");
  const request: unknown = await response.json();
  const restrictions = sentRestrictionsFromRequest(request);
  if (!restrictions) throw new Error("The archived generation request is malformed.");
  return {
    restrictions,
    coreRange: coreRangeFromRequest(request),
    reviewCoreRange: reviewCoreRangeFromRequest(request),
    blocking: blockingFromRequest(request),
    minDays: sentMinDaysFromRequest(request),
  };
};
